import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { Dayjs } from 'dayjs';
import { Holiday, DaySchedule } from '../entity';
import { TimeSlot } from '@features/visits/schedule/entity';

type DayEditDialogProps = {
  open: boolean;
  onClose: () => void;
  selectedDate: Dayjs | null;
  currentHoliday?: Holiday;
  currentDayOverride?: DaySchedule;
  defaultDaySchedule: DaySchedule;
  onSave: (holiday?: Holiday, dayOverride?: DaySchedule) => void;
};

const DayEditDialog = ({
  open,
  onClose,
  selectedDate,
  currentHoliday,
  currentDayOverride,
  defaultDaySchedule,
  onSave,
}: DayEditDialogProps) => {
  const [isHoliday, setIsHoliday] = useState(false);
  const [holidayName, setHolidayName] = useState('');
  const [isPartialDay, setIsPartialDay] = useState(false);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('17:00');

  const [isCustomHours, setIsCustomHours] = useState(false);
  const [isWorking, setIsWorking] = useState(true);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([{ start: '08:00', end: '20:00' }]);

  useEffect(() => {
    if (open && selectedDate) {
      if (currentHoliday) {
        setIsHoliday(true);
        setHolidayName(currentHoliday.name || '');
        setIsPartialDay(!!currentHoliday.startTime && !!currentHoliday.endTime);
        setStartTime(currentHoliday.startTime || '08:00');
        setEndTime(currentHoliday.endTime || '17:00');
        setIsCustomHours(false);
      } else if (currentDayOverride) {
        setIsHoliday(false);
        setIsCustomHours(true);
        setIsWorking(currentDayOverride.isWorking);
        setTimeSlots(
          currentDayOverride.timeSlots.length > 0
            ? currentDayOverride.timeSlots.map(s => ({ ...s }))
            : [{ start: '08:00', end: '20:00' }],
        );
      } else {
        setIsHoliday(false);
        setHolidayName('');
        setIsPartialDay(false);
        setStartTime('08:00');
        setEndTime('17:00');
        setIsCustomHours(false);
        setIsWorking(defaultDaySchedule.isWorking);
        setTimeSlots(
          defaultDaySchedule.timeSlots.length > 0
            ? defaultDaySchedule.timeSlots.map(s => ({ ...s }))
            : [{ start: '08:00', end: '20:00' }],
        );
      }
    }
  }, [open, selectedDate, currentHoliday, currentDayOverride, defaultDaySchedule]);

  const handleToggleHoliday = (checked: boolean) => {
    setIsHoliday(checked);
    if (checked) setIsCustomHours(false);
  };

  const handleToggleCustomHours = (checked: boolean) => {
    setIsCustomHours(checked);
    if (checked) setIsHoliday(false);
  };

  const handleAddTimeSlot = () => {
    setTimeSlots([...timeSlots, { start: '08:00', end: '20:00' }]);
  };

  const handleRemoveTimeSlot = (index: number) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const handleTimeSlotChange = (index: number, field: 'start' | 'end', value: string) => {
    setTimeSlots(timeSlots.map((slot, i) => (i === index ? { ...slot, [field]: value } : slot)));
  };

  const handleSave = () => {
    if (!selectedDate) return;

    if (isHoliday) {
      const holiday: Holiday = {
        date: selectedDate.format('YYYY-MM-DD'),
        name: holidayName || undefined,
        startTime: isPartialDay ? startTime : undefined,
        endTime: isPartialDay ? endTime : undefined,
      };
      onSave(holiday, undefined);
    } else if (isCustomHours) {
      const dayOverride: DaySchedule = {
        isWorking,
        timeSlots: isWorking ? timeSlots : [],
      };
      onSave(undefined, dayOverride);
    } else {
      onSave(undefined, undefined);
    }

    onClose();
  };

  if (!selectedDate) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="workSchedule.editDay" defaultMessage="Upravit den" />
        {' - '}
        {selectedDate.format('DD. MMMM YYYY')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch checked={isHoliday} onChange={e => handleToggleHoliday(e.target.checked)} />
            }
            label={
              <FormattedMessage
                id="workSchedule.isHoliday"
                defaultMessage="Označit jako dovolenou/svátek"
              />
            }
          />

          {isHoliday && (
            <Box sx={{ mt: 2, ml: 1 }}>
              <TextField
                fullWidth
                label={
                  <FormattedMessage
                    id="workSchedule.holidayName"
                    defaultMessage="Název svátku (volitelné)"
                  />
                }
                value={holidayName}
                onChange={e => setHolidayName(e.target.value)}
                placeholder="např. Vánoce, Dovolená"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={isPartialDay}
                    onChange={e => setIsPartialDay(e.target.checked)}
                  />
                }
                label={
                  <FormattedMessage
                    id="workSchedule.partialDayHoliday"
                    defaultMessage="Pouze část dne"
                  />
                }
              />

              {isPartialDay && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <TextField
                    type="time"
                    label={<FormattedMessage id="workSchedule.from" defaultMessage="Od" />}
                    value={startTime}
                    onChange={e => setStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="time"
                    label={<FormattedMessage id="workSchedule.to" defaultMessage="Do" />}
                    value={endTime}
                    onChange={e => setEndTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isCustomHours}
                  onChange={e => handleToggleCustomHours(e.target.checked)}
                />
              }
              label={
                <FormattedMessage
                  id="workSchedule.customHours"
                  defaultMessage="Vlastní pracovní doba pro tento den"
                />
              }
            />

            {isCustomHours && (
              <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
                <FormattedMessage
                  id="workSchedule.customHoursOnlyThisDay"
                  defaultMessage="Tato úprava platí pouze pro {date}. Ostatní dny tohoto týdne zůstanou nezměněny."
                  values={{ date: selectedDate?.format('DD. MMMM YYYY') }}
                />
              </Alert>
            )}

            {isCustomHours && (
              <Box sx={{ mt: 2, ml: 1 }}>
                <FormControlLabel
                  control={
                    <Switch checked={isWorking} onChange={e => setIsWorking(e.target.checked)} />
                  }
                  label={
                    <FormattedMessage id="workSchedule.isWorking" defaultMessage="Pracovní den" />
                  }
                />

                {isWorking && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <FormattedMessage
                        id="workSchedule.workingHours"
                        defaultMessage="Pracovní hodiny"
                      />
                    </Typography>
                    <Stack spacing={2}>
                      {timeSlots.map((slot, index) => (
                        <Stack key={index} direction="row" spacing={1} alignItems="center">
                          <TextField
                            type="time"
                            label={<FormattedMessage id="workSchedule.from" defaultMessage="Od" />}
                            value={slot.start}
                            onChange={e => handleTimeSlotChange(index, 'start', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            type="time"
                            label={<FormattedMessage id="workSchedule.to" defaultMessage="Do" />}
                            value={slot.end}
                            onChange={e => handleTimeSlotChange(index, 'end', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          {timeSlots.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveTimeSlot(index)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                      <Button
                        startIcon={<Add />}
                        onClick={handleAddTimeSlot}
                        variant="outlined"
                        size="small"
                      >
                        <FormattedMessage
                          id="workSchedule.addTimeSlot"
                          defaultMessage="Přidat časový úsek"
                        />
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage id="common.cancel" defaultMessage="Zrušit" />
        </Button>
        <Button onClick={handleSave} variant="contained">
          <FormattedMessage id="common.save" defaultMessage="Uložit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DayEditDialog;
